import React, { useState, useRef } from 'react';

import {
  Flex,
  Box,
  IconButton,
  Input,
  Text,
  Tooltip,
  InputGroup,
  InputRightElement,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from '@vimeo/bokeh';
import {
  ChevronLeft,
  ListUlFilled,
  EllipsisV,
  PopOutFilled,
  SearchMagnifier,
  CloseXCircleFilled,
  CloseXSmall,
} from '@vimeo/bokeh-icons';

import { useHelpPanel } from '../hooks/useHelpPanel';
import { useTrackHelpPanelEvent } from '../utils/bigPicture';
import { truncateContent } from '../utils/helper';
import { ContactUsMenu } from './ContactUsMenu';

interface PanelHeaderProps {
  title: string;
  showBackButton: boolean;
  showSearchButton: boolean;
  showContactUsMenu: boolean;
  showViewRequestsButton: boolean;
}

export const PanelHeader: React.FC<PanelHeaderProps> = ({
  title,
  showBackButton,
  showSearchButton,
  showContactUsMenu,
  showViewRequestsButton,
}) => {
  const { goBack, goToPage, currentHCUrl, handleSearch, isSearching, hasEnterprise, closePanel } =
    useHelpPanel();
  const trackHelpPanelEvent = useTrackHelpPanelEvent();
  const [searchQuery, setSearchQuery] = useState('');
  const truncatedTitle = truncateContent(title, 20);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const emptySearchQuery = searchQuery ? (
    <CloseXCircleFilled
      onClick={() => {
        setSearchQuery('');
        inputRef.current?.focus();
      }}
      tabIndex={0}
      aria-label="Clear search"
      role="button"
      boxSize={20}
      mr="sm"
    />
  ) : undefined;

  const onSearch = () => {
    if (searchQuery.trim()) {
      goToPage('search', []);
      handleSearch(searchQuery);
      trackHelpPanelEvent({
        eventName: 'vimeo.in_app_support_search',
        element: 'search_bar',
        copy: searchQuery,
        flow: 'search',
      });
    }
  };

  const keyDownHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onSearch();
      event.preventDefault();
    }
  };

  const ResourcesMenu = (
    <Menu placement="bottom-end">
      <MenuButton
        as={IconButton}
        aria-label="Resources"
        icon={<EllipsisV />}
        variant="tertiary"
        size="sm"
      />
      <MenuList width={285} height={205}>
        <MenuItem
          as="a"
          href="https://vimeo.com/blog/post/spring-2025-release?utm_source=resource-center&utm_medium=pendo&utm_campaign=Spring-Release-2025"
          target="_blank"
          height={62}
          width={260}
          onClick={() =>
            trackHelpPanelEvent({
              eventName: 'vimeo.in_app_support_panel_button_click',
              element: 'button',
              copy: 'spring 2025 release',
            })
          }
        >
          <Box>
            <Text variant="heading-xs">Spring 2025 release</Text>
            <Text variant="body-sm">Explore the latest product releases</Text>
          </Box>
        </MenuItem>
        <MenuItem
          as="a"
          href="https://vimeo.com/integrations"
          target="_blank"
          height={62}
          width={260}
          onClick={() =>
            trackHelpPanelEvent({
              eventName: 'vimeo.in_app_support_panel_button_click',
              element: 'button',
              copy: 'integrations hub',
            })
          }
        >
          <Box>
            <Text variant="heading-xs">Integrations hub</Text>
            <Text variant="body-sm">Browse apps and integrations</Text>
          </Box>
        </MenuItem>
        <MenuItem
          as="a"
          href="https://vimeo.com/events"
          target="_blank"
          height={62}
          width={260}
          onClick={() =>
            trackHelpPanelEvent({
              eventName: 'vimeo.in_app_support_panel_button_click',
              element: 'button',
              copy: 'live and on-demand events',
            })
          }
        >
          <Box>
            <Text variant="heading-xs">Live and on-demand events</Text>
            <Text variant="body-sm">Search talks and trainings</Text>
          </Box>
        </MenuItem>
      </MenuList>
    </Menu>
  );

  return (
    <Box>
      <Flex alignItems="center" justifyContent="space-between">
        {showBackButton && (
          <IconButton
            icon={<ChevronLeft />}
            aria-label="Back"
            onClick={goBack}
            variant="tertiary"
            size="sm"
            mr={2}
          />
        )}
        <Flex alignItems="center" flex="1">
          <Text variant="heading-sm">{truncatedTitle}</Text>
          <Tooltip label="View in Help Center" placement="bottom">
            <IconButton
              as="a"
              href={`https://vimeo.com/help/sso?redirect_to=${currentHCUrl}`}
              target="_blank"
              rel="noopener noreferrer"
              icon={<PopOutFilled />}
              aria-label="Go to Help Center"
              variant="tertiary"
              ml={2}
              size="sm"
              onClick={() =>
                trackHelpPanelEvent({
                  eventName: 'vimeo.in_app_support_panel_button_click',
                  element: 'button',
                  copy: 'view in Help Center',
                })
              }
            />
          </Tooltip>
        </Flex>
        {showContactUsMenu && (
          <Flex justifyContent="space-between" alignItems="center">
            <Box w="md" />
            <ContactUsMenu />
          </Flex>
        )}
        {showViewRequestsButton && (
          <Tooltip label="View requests" placement="bottom">
            <IconButton
              as="a"
              href={`https://vimeo.com/help/sso?redirect_to=${currentHCUrl}/requests`}
              target="_blank"
              rel="noopener noreferrer"
              icon={<ListUlFilled />}
              aria-label="Requests"
              variant="tertiary"
              size="sm"
              onClick={() =>
                trackHelpPanelEvent({
                  eventName: 'vimeo.in_app_support_panel_button_click',
                  element: 'button',
                  copy: 'view requests',
                })
              }
            />
          </Tooltip>
        )}
        {hasEnterprise && (
          <Tooltip label="Resources" placement="bottom">
            {ResourcesMenu}
          </Tooltip>
        )}
        <IconButton
          icon={<CloseXSmall />}
          aria-label="Close help panel"
          variant="tertiary"
          size="sm"
          onClick={closePanel}
          ml={2}
          display={{ base: 'inline-flex', sm: 'none' }}
        />
      </Flex>
      {showSearchButton && (
        <Box mt={4}>
          <InputGroup>
            <Input
              placeholder="Search for help"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={keyDownHandler}
              isDisabled={isSearching}
              pr="60px"
              ref={inputRef}
            />
            <InputRightElement
              _hover={{
                backgroundColor: 'var(--vimeo-colors-darkBlueAlpha-200)',
              }}
              borderRadius="md"
              margin="xs"
              width="var(--vimeo-sizes-sm)"
              height="var(--vimeo-sizes-sm)"
            >
              {emptySearchQuery}
              <SearchMagnifier
                mr={emptySearchQuery ? 6 : 0}
                boxSize={20}
                tabIndex={0}
                aria-label="Search"
                role="button"
                onClick={onSearch}
              />
            </InputRightElement>
          </InputGroup>
        </Box>
      )}
    </Box>
  );
};
